import { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import api from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Checkbox } from '@/components/ui/checkbox';
import { Textarea } from '@/components/ui/textarea';
import {
  Plus, Search, User, ChevronDown, Wallet, Calendar,
  Loader2, Check, ChevronRight, PlusCircle, X, ArrowUpDown, ArrowUp, ArrowDown,
  FileSpreadsheet, FileDown
} from 'lucide-react';
import { ekstreIndir } from '@/lib/ekstre';
import { toast } from 'sonner';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { trNorm } from '@/lib/arama';

export default function EmployeeList() {
  const [employees, setEmployees] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [positions, setPositions] = useState([]);

  const [search, setSearch] = useState('');
  const [deptFilter, setDeptFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('active');
  const [sortCol, setSortCol] = useState('full_name');
  const [sortDir, setSortDir] = useState('asc');
  const [showNew, setShowNew] = useState(false);
  const [selectedEmployees, setSelectedEmployees] = useState([]);
  const [form, setForm] = useState({
    first_name: '', last_name: '', national_id: '', birth_date: '', gender: 'male',
    phone: '', email: '', address: '', department_id: '', position_id: '',
    employment_type: 'full_time', hire_date: '', insurance_number: '',
    bank_name: '', bank_account_number: '',
    emergency_contact_name: '', emergency_contact_phone: ''
  });
  const [loading, setLoading] = useState(false);
  
  // İşlem dialog'ları
  const [showAdvance, setShowAdvance] = useState(false);
  const [showLeave, setShowLeave] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);
  
  // Form state'leri
  const [advanceForm, setAdvanceForm] = useState({ amount: '', reason: '', request_date: new Date().toISOString().split('T')[0] });
  const [leaveForm, setLeaveForm] = useState({ leave_type: 'annual', start_date: '', end_date: '', reason: '' });

  useEffect(() => {
    api.get('/hr/employees?include_inactive=true').then(r => setEmployees(r.data)).catch(() => {});
    api.get('/hr/departments').then(r => setDepartments(r.data)).catch(() => {});
    api.get('/hr/positions').then(r => setPositions(r.data)).catch(() => {});
  }, []);

  const handleCreate = async () => {
    if (!form.first_name.trim() || !form.last_name.trim()) { toast.error('Ad ve soyad zorunlu'); return; }
    setLoading(true);
    try {
      await api.post('/hr/employees', form);
      toast.success('Personel kaydedildi');
      setShowNew(false);
      api.get('/hr/employees').then(r => setEmployees(r.data));
    } catch (err) { toast.error(err.response?.data?.detail || 'Hata'); }
    setLoading(false);
  };

  const handleSort = (col) => {
    if (sortCol === col) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortCol(col); setSortDir('asc'); }
  };

  const filtered = employees
    .filter(e => {
      if (search) {
        const sl = trNorm(search);
        if (!trNorm(e.full_name).includes(sl) && !(e.national_id || '').includes(sl)) return false;
      }
      if (deptFilter !== 'all' && e.department_id !== deptFilter) return false;
      if (statusFilter === 'active' && !e.is_active) return false;
      if (statusFilter === 'inactive' && e.is_active) return false;
      return true;
    })
    .sort((a, b) => {
      let av = a[sortCol] ?? '';
      let bv = b[sortCol] ?? '';
      if (sortCol === 'current_salary') { av = av || 0; bv = bv || 0; }
      const cmp = typeof av === 'number' ? av - bv : String(av).localeCompare(String(bv), 'tr');
      return sortDir === 'asc' ? cmp : -cmp;
    });

  // Döküm EKRANDAKİ süzgeç ve SIRAYLA sınırlı: görünen satırların id'leri
  // gönderilir, rakamları sunucu kendisi hesaplar (maaş kapsamı da orada).
  const listeDokumu = (bicim) => ekstreIndir({
    yol: '/hr/employees/liste-dokumu',
    bicim,
    satirIdler: filtered.map(e => e.id),
    filtre: [
      `Departman: ${deptFilter === 'all' ? 'Tümü'
        : (departments.find(d => d.id === deptFilter)?.name || deptFilter)}`,
      `Durum: ${statusFilter === 'all' ? 'Tümü' : statusFilter === 'active' ? 'Aktif' : 'Pasif'}`,
      search ? `Arama: ${search}` : null,
    ].filter(Boolean).join(' · '),
    bosMesaj: 'Süzgece uyan personel yok',
    tur: 'listesi',
  });

  const empTypes = { full_time: 'Tam Zamanli', part_time: 'Yari Zamanli', contractor: 'Sozlesmeli' };
  const empTypeStyles = { full_time: 'bg-[#3d6e4e]/10 text-[#3d6e4e]', part_time: 'bg-[#7a6a2e]/10 text-[#7a6a2e]', contractor: 'bg-[#4a5a8e]/10 text-[#4a5a8e]' };
  const leaveTypes = { annual: 'Yıllık İzin', sick: 'Hastalık İzni', unpaid: 'Ücretsiz İzin', maternity: 'Doğum İzni', other: 'Diğer' };

  // Seçim fonksiyonları
  const toggleSelectAll = () => {
    if (selectedEmployees.length === filtered.length) {
      setSelectedEmployees([]);
    } else {
      setSelectedEmployees(filtered.map(e => e.id));
    }
  };

  const toggleSelect = (id) => {
    setSelectedEmployees(prev => 
      prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]
    );
  };

  const getSelectedNames = () => {
    return selectedEmployees.map(id => employees.find(e => e.id === id)?.full_name).filter(Boolean);
  };

  // İşlem fonksiyonları
  const handleAdvanceSubmit = async () => {
    if (selectedEmployees.length !== 1) { toast.error('Avans talebi için tek personel seçin'); return; }
    if (!advanceForm.amount) { toast.error('Tutar zorunlu'); return; }
    setActionLoading(true);
    try {
      await api.post('/hr/advances', {
        employee_id: selectedEmployees[0],
        amount: parseFloat(advanceForm.amount),
        reason: advanceForm.reason,
        request_date: advanceForm.request_date
      });
      toast.success('Avans talebi oluşturuldu');
      setShowAdvance(false);
      setAdvanceForm({ amount: '', reason: '', request_date: new Date().toISOString().split('T')[0] });
      setSelectedEmployees([]);
    } catch (err) { toast.error(err.response?.data?.detail || 'Hata'); }
    setActionLoading(false);
  };

  const handleLeaveSubmit = async () => {
    if (selectedEmployees.length !== 1) { toast.error('İzin talebi için tek personel seçin'); return; }
    if (!leaveForm.start_date || !leaveForm.end_date) { toast.error('Tarihler zorunlu'); return; }
    setActionLoading(true);
    try {
      await api.post('/hr/leaves', {
        employee_id: selectedEmployees[0],
        leave_type: leaveForm.leave_type,
        start_date: leaveForm.start_date,
        end_date: leaveForm.end_date,
        reason: leaveForm.reason
      });
      toast.success('İzin talebi oluşturuldu');
      setShowLeave(false);
      setLeaveForm({ leave_type: 'annual', start_date: '', end_date: '', reason: '' });
      setSelectedEmployees([]);
    } catch (err) { toast.error(err.response?.data?.detail || 'Hata'); }
    setActionLoading(false);
  };

  return (
    <div data-testid="employee-list-page">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-3xl font-bold text-[#1a1916] tracking-tight">Personeller</h1>
          <p className="text-sm text-[#8a7e70] mt-1">
            {filtered.length} personel
            {selectedEmployees.length > 0 && ` • ${selectedEmployees.length} seçili`}
          </p>
        </div>
        <div className="flex gap-2">
          {selectedEmployees.length > 0 && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" className="border-[#d0c4b0] text-[#3F3E39]">
                  İşlemler <ChevronDown size={14} className="ml-2" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-48">
                <DropdownMenuItem 
                  onClick={() => setShowAdvance(true)}
                  disabled={selectedEmployees.length !== 1}
                  className="cursor-pointer"
                >
                  <Wallet size={14} className="mr-2" /> Avans Talebi
                  {selectedEmployees.length !== 1 && <span className="ml-auto text-xs text-muted-foreground">(tek)</span>}
                </DropdownMenuItem>
                <DropdownMenuItem 
                  onClick={() => setShowLeave(true)}
                  disabled={selectedEmployees.length !== 1}
                  className="cursor-pointer"
                >
                  <Calendar size={14} className="mr-2" /> İzin Talebi
                  {selectedEmployees.length !== 1 && <span className="ml-auto text-xs text-muted-foreground">(tek)</span>}
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          )}
          <Button variant="outline" onClick={() => listeDokumu('xlsx')}
            className="border-[#d0c4b0] text-[#3d6e4e] hover:bg-[#3d6e4e]/10"
            title="Ekrandaki listeyi Excel olarak indir" data-testid="liste-excel">
            <FileSpreadsheet size={16} className="mr-2" /> Excel
          </Button>
          <Button variant="outline" onClick={() => listeDokumu('pdf')}
            className="border-[#d0c4b0] text-[#7a3535] hover:bg-[#7a3535]/10"
            title="Ekrandaki listeyi PDF olarak indir" data-testid="liste-pdf">
            <FileDown size={16} className="mr-2" /> PDF
          </Button>
          <Button onClick={() => setShowNew(true)} className="bg-[#2e2d29] text-[#f5f0e8] hover:bg-[#1a1916] font-semibold" data-testid="new-employee-btn">
            <Plus size={16} className="mr-2" /> Yeni Personel
          </Button>
        </div>
      </div>

      <div className="flex flex-wrap gap-3 mb-6">
        <div className="relative flex-1 min-w-[200px] max-w-sm">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#8a7e70]" />
          <Input placeholder="Personel ara..." value={search} onChange={(e) => setSearch(e.target.value)}
            className="pl-9 bg-white border-[#d0c4b0]" data-testid="employee-search" />
        </div>
        <Select value={deptFilter} onValueChange={setDeptFilter}>
          <SelectTrigger className="w-44 bg-white border-[#d0c4b0]"><SelectValue placeholder="Departman" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Tum Departmanlar</SelectItem>
            {departments.map(d => <SelectItem key={d.id} value={d.id}>{d.name}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-36 bg-white border-[#d0c4b0]"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Tümü</SelectItem>
            <SelectItem value="active">Aktif</SelectItem>
            <SelectItem value="inactive">Pasif</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <div className="bg-[#faf7f2] border border-[#d0c4b0] rounded-lg overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow className="bg-[#e8dfd0] hover:bg-[#e8dfd0]">
              <TableHead className="w-10">
                <Checkbox
                  checked={filtered.length > 0 && selectedEmployees.length === filtered.length}
                  onCheckedChange={toggleSelectAll}
                />
              </TableHead>
              {[
                { col: 'full_name', label: 'Ad Soyad' },
                { col: 'department_name', label: 'Departman' },
                { col: 'position_name', label: 'Pozisyon' },
                { col: 'employment_type', label: 'Çalışma Türü' },
                { col: 'hire_date', label: 'İşe Giriş' },
                { col: 'termination_date', label: 'İşten Çıkış' },
                { col: 'current_salary', label: 'Güncel Maaş', right: true },
                { col: 'is_active', label: 'Durum' },
              ].map(({ col, label, right }) => (
                <TableHead key={col} className={`text-[#1a1916] font-bold text-xs uppercase cursor-pointer select-none hover:bg-[#d8cfc0] transition-colors ${right ? 'text-right' : ''}`} onClick={() => handleSort(col)}>
                  <span className="inline-flex items-center gap-1">
                    {label}
                    {sortCol === col
                      ? sortDir === 'asc' ? <ArrowUp size={11} /> : <ArrowDown size={11} />
                      : <ArrowUpDown size={11} className="text-[#b0a898]" />}
                  </span>
                </TableHead>
              ))}
            </TableRow>
          </TableHeader>
          <TableBody>
            {filtered.map(e => (
              <TableRow key={e.id} className={`erp-table-row border-[#d0c4b0] ${selectedEmployees.includes(e.id) ? 'bg-[#e8dfd0]/50' : ''}`}>
                <TableCell>
                  <Checkbox 
                    checked={selectedEmployees.includes(e.id)}
                    onCheckedChange={() => toggleSelect(e.id)}
                  />
                </TableCell>
                <TableCell>
                  <Link to={`/erp/hr/employees/${e.id}`} className="font-medium text-[#1a1916] hover:underline flex items-center gap-2" data-testid={`emp-link-${e.id}`}>
                    <div className="w-7 h-7 rounded-full bg-[#2e2d29]/10 flex items-center justify-center flex-shrink-0">
                      <User size={14} className="text-[#2e2d29]" />
                    </div>
                    {e.full_name}
                  </Link>
                </TableCell>
                <TableCell className="text-sm text-[#3F3E39]">{e.department_name || '-'}</TableCell>
                <TableCell className="text-sm text-[#3F3E39]">{e.position_name || '-'}</TableCell>
                <TableCell className="text-xs"><span className={`status-badge ${empTypeStyles[e.employment_type] || 'bg-[#e8dfd0] text-[#3F3E39]'}`}>{empTypes[e.employment_type] || e.employment_type}</span></TableCell>
                <TableCell className="text-sm text-[#3F3E39]">{e.hire_date || '-'}</TableCell>
                <TableCell className="text-sm text-[#8a7e70]">{e.termination_date || <span className="text-[#c0b8ad]">—</span>}</TableCell>
                <TableCell className="text-right font-mono text-sm font-semibold">
                  {e.current_salary != null
                    ? <span className="text-[#1a1916]">{e.current_salary.toLocaleString('tr-TR')} <span className="text-[9px] text-[#8a7e70] font-normal">{e.salary_currency}</span></span>
                    : <span className="text-[#c0b8ad]">—</span>}
                </TableCell>
                <TableCell>
                  <span className={`status-badge ${e.is_active ? 'bg-[#3d6e4e]/10 text-[#3d6e4e]' : 'bg-[#7a3535]/10 text-[#7a3535]'}`}>
                    {e.is_active ? 'Aktif' : 'Pasif'}
                  </span>
                </TableCell>
              </TableRow>
            ))}
            {filtered.length === 0 && <TableRow><TableCell colSpan={9} className="text-center py-8 text-[#8a7e70]">Personel bulunamadi</TableCell></TableRow>}
          </TableBody>
        </Table>
      </div>

      {/* New Employee Dialog — 2 adımlı */}
      <NewEmployeeDialog
        open={showNew}
        onClose={() => setShowNew(false)}
        onSave={handleCreate}
        form={form}
        setForm={setForm}
        loading={loading}
        departments={departments}
        positions={positions}
        setDepartments={setDepartments}
        setPositions={setPositions}
      />

      {/* Avans Talebi Dialog */}
      <Dialog open={showAdvance} onOpenChange={setShowAdvance}>
        <DialogContent className="bg-[#faf7f2] border-[#d0c4b0] max-w-md">
          <DialogHeader>
            <DialogTitle className="text-[#1a1916] font-bold">Avans Talebi</DialogTitle>
          </DialogHeader>
          <div className="text-sm text-[#8a7e70] mb-4">
            Personel: <span className="font-medium text-[#1a1916]">{getSelectedNames().join(', ')}</span>
          </div>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Tarih</Label>
              <Input type="date" value={advanceForm.request_date} onChange={(e) => setAdvanceForm(p => ({...p, request_date: e.target.value}))} className="bg-white border-[#d0c4b0]" />
            </div>
            <div className="space-y-2">
              <Label>Tutar *</Label>
              <Input type="number" step="0.01" value={advanceForm.amount} onChange={(e) => setAdvanceForm(p => ({...p, amount: e.target.value}))} placeholder="0.00" className="bg-white border-[#d0c4b0]" />
            </div>
            <div className="space-y-2">
              <Label>Açıklama</Label>
              <Textarea value={advanceForm.reason} onChange={(e) => setAdvanceForm(p => ({...p, reason: e.target.value}))} className="bg-white border-[#d0c4b0]" rows={2} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAdvance(false)}>İptal</Button>
            <Button onClick={handleAdvanceSubmit} disabled={actionLoading} className="bg-[#3d6e4e] text-white hover:bg-[#2e5a3e]">
              {actionLoading ? <Loader2 className="animate-spin mr-2" size={16} /> : null}
              Talep Oluştur
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* İzin Talebi Dialog */}
      <Dialog open={showLeave} onOpenChange={setShowLeave}>
        <DialogContent className="bg-[#faf7f2] border-[#d0c4b0] max-w-md">
          <DialogHeader>
            <DialogTitle className="text-[#1a1916] font-bold">İzin Talebi</DialogTitle>
          </DialogHeader>
          <div className="text-sm text-[#8a7e70] mb-4">
            Personel: <span className="font-medium text-[#1a1916]">{getSelectedNames().join(', ')}</span>
          </div>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>İzin Türü</Label>
              <Select value={leaveForm.leave_type} onValueChange={(v) => setLeaveForm(p => ({...p, leave_type: v}))}>
                <SelectTrigger className="bg-white border-[#d0c4b0]"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {Object.entries(leaveTypes).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Başlangıç *</Label>
                <Input type="date" value={leaveForm.start_date} onChange={(e) => setLeaveForm(p => ({...p, start_date: e.target.value}))} className="bg-white border-[#d0c4b0]" />
              </div>
              <div className="space-y-2">
                <Label>Bitiş *</Label>
                <Input type="date" value={leaveForm.end_date} onChange={(e) => setLeaveForm(p => ({...p, end_date: e.target.value}))} className="bg-white border-[#d0c4b0]" />
              </div>
            </div>
            <div className="space-y-2">
              <Label>Açıklama</Label>
              <Textarea value={leaveForm.reason} onChange={(e) => setLeaveForm(p => ({...p, reason: e.target.value}))} className="bg-white border-[#d0c4b0]" rows={2} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowLeave(false)}>İptal</Button>
            <Button onClick={handleLeaveSubmit} disabled={actionLoading} className="bg-[#3d6e4e] text-white hover:bg-[#2e5a3e]">
              {actionLoading ? <Loader2 className="animate-spin mr-2" size={16} /> : null}
              Talep Oluştur
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

    </div>
  );
}

// ─── Creatable Combobox (inline) ───
function CreatableCombobox({ label, value, onChange, options, onCreateOption, placeholder = 'Ara veya ekle...' }) {
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);
  const [creating, setCreating] = useState(false);
  const containerRef = useRef(null);
  const inputRef = useRef(null);

  const selectedLabel = options.find(o => o.value === value)?.label || '';

  useEffect(() => {
    const h = (e) => { if (containerRef.current && !containerRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, []);
  useEffect(() => { if (open) { setQuery(''); inputRef.current?.focus(); } }, [open]);

  const filtered = options.filter(o => trNorm(o.label).includes(trNorm(query)));
  const exactMatch = options.some(o => trNorm(o.label) === trNorm(query));
  const showCreate = query.trim().length > 0 && !exactMatch;

  const handleCreate = async () => {
    if (!query.trim() || creating) return;
    setCreating(true);
    try {
      const newOpt = await onCreateOption(query.trim());
      onChange(newOpt.value);
      setOpen(false); setQuery('');
    } catch { toast.error('Eklenemedi'); }
    setCreating(false);
  };

  return (
    <div className="space-y-1.5" ref={containerRef}>
      <Label className="text-sm font-medium text-[#3F3E39]">{label}</Label>
      <div className="relative">
        <button type="button" onClick={() => setOpen(v => !v)}
          className="w-full h-9 px-3 pr-8 rounded-md border border-[#d0c4b0] bg-white text-sm text-left flex items-center justify-between hover:border-[#8a7e70] focus:outline-none focus:ring-2 focus:ring-[#2e2d29]/15 transition-colors">
          <span className={selectedLabel ? 'text-[#1a1916] font-medium' : 'text-[#8a7e70]'}>{selectedLabel || placeholder}</span>
          <div className="flex items-center gap-1">
            {value && <span onClick={e => { e.stopPropagation(); onChange(null); }} className="text-[#8a7e70] hover:text-[#7a3535] p-0.5 rounded cursor-pointer"><X size={11} /></span>}
            <ChevronDown size={12} className={`text-[#8a7e70] transition-transform ${open ? 'rotate-180' : ''}`} />
          </div>
        </button>
        {open && (
          <div className="absolute z-50 mt-1 w-full bg-white border border-[#d0c4b0] rounded-md shadow-lg overflow-hidden animate-fade-in">
            <div className="flex items-center gap-2 px-2.5 py-2 border-b border-[#e8dfd0]">
              <Search size={12} className="text-[#8a7e70] flex-shrink-0" />
              <input ref={inputRef} value={query} onChange={e => setQuery(e.target.value)}
                placeholder="Ara veya yeni ad yazın…" className="flex-1 text-sm outline-none bg-transparent text-[#1a1916] placeholder:text-[#8a7e70]"
                onKeyDown={e => { if (e.key === 'Escape') setOpen(false); if (e.key === 'Enter' && showCreate) handleCreate(); }} />
            </div>
            <div className="max-h-40 overflow-y-auto">
              {filtered.map(o => (
                <button key={o.value} type="button" onClick={() => { onChange(o.value); setOpen(false); setQuery(''); }}
                  className={`w-full text-left px-3 py-2 text-sm flex items-center justify-between hover:bg-[#e8dfd0] transition-colors ${value === o.value ? 'bg-[#e8dfd0] font-semibold text-[#1a1916]' : 'text-[#3F3E39]'}`}>
                  {o.label} {value === o.value && <Check size={12} className="text-[#3d6e4e]" />}
                </button>
              ))}
              {/* Bos listede "Sonuc yok" demek cikmaz sokaga sokuyordu: yeni bir
                  sirkette departman/pozisyon listesi bombos baslıyor ve kullanici
                  buradan EKLEYEBILECEGINI hic anlamiyordu (2026-09-15'te bildirildi).
                  Bos durum artik ne yapilacagini soyluyor. */}
              {filtered.length === 0 && !showCreate && (
                <p className="text-xs text-[#8a7e70] px-3 py-3 text-center">
                  {options.length === 0
                    ? 'Henüz kayıt yok — yeni eklemek için yukarıya yazın.'
                    : 'Sonuç yok — yeni eklemek için yazmaya devam edin.'}
                </p>
              )}
              {showCreate && (
                <button type="button" onClick={handleCreate} disabled={creating}
                  className="w-full text-left px-3 py-2 text-sm flex items-center gap-2 text-[#3d6e4e] font-semibold hover:bg-[#3d6e4e]/8 border-t border-[#e8dfd0] transition-colors">
                  <PlusCircle size={12} /> {creating ? 'Ekleniyor...' : `"${query.trim()}" ekle`}
                </button>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// ─── 2 Adımlı Yeni Personel Dialog ───
function NewEmployeeDialog({ open, onClose, onSave, form, setForm, loading, departments, positions, setDepartments, setPositions }) {
  const [step, setStep] = useState(1);

  useEffect(() => { if (open) setStep(1); }, [open]);

  const canNext = form.first_name.trim() && form.last_name.trim();

  const createDepartment = async (name) => {
    const res = await api.post('/hr/departments', { name });
    setDepartments(prev => [...prev, res.data]);
    toast.success(`"${name}" departmanı eklendi`);
    return { value: res.data.id, label: res.data.name };
  };
  const createPosition = async (name) => {
    const res = await api.post('/hr/positions', { name, department_id: form.department_id || null });
    setPositions(prev => [...prev, res.data]);
    toast.success(`"${name}" pozisyonu eklendi`);
    return { value: res.data.id, label: res.data.name };
  };

  const fi = (name, type = 'text') => ({
    type, value: form[name] || '',
    onChange: e => setForm(p => ({ ...p, [name]: e.target.value })),
    className: 'bg-white border-[#d0c4b0] h-9',
    'data-testid': `emp-${name}`,
  });
  const lbl = (text, required) => (
    <Label className="text-sm font-medium text-[#3F3E39]">{text}{required && <span className="text-[#7a3535] ml-0.5">*</span>}</Label>
  );

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="bg-[#faf7f2] border-[#d0c4b0] max-w-lg p-0 gap-0 overflow-hidden">

        {/* Header */}
        <div className="px-6 pt-5 pb-4 border-b border-[#e8dfd0]">
          <div className="flex items-center justify-between mb-3">
            <DialogTitle className="text-lg font-bold text-[#1a1916]">Yeni Personel</DialogTitle>
            <div className="flex items-center gap-1.5">
              {[1, 2].map(s => (
                <button key={s} onClick={() => s < step || canNext ? setStep(s) : null}
                  className={`flex items-center justify-center w-6 h-6 rounded-full text-xs font-bold transition-all ${
                    s === step ? 'bg-[#2e2d29] text-[#f5f0e8]' :
                    s < step ? 'bg-[#3d6e4e] text-white cursor-pointer' :
                    'bg-[#e8dfd0] text-[#8a7e70]'
                  }`}>
                  {s < step ? <Check size={11} /> : s}
                </button>
              ))}
              <span className="text-xs text-[#8a7e70] ml-1">{step === 1 ? 'Temel Bilgiler' : 'Ek Bilgiler'}</span>
            </div>
          </div>
          <div className="h-1 bg-[#e8dfd0] rounded-full">
            <div className="h-1 bg-[#2e2d29] rounded-full transition-all duration-300" style={{ width: step === 1 ? '50%' : '100%' }} />
          </div>
        </div>

        {/* Body */}
        <div className="px-6 py-5">
          {step === 1 ? (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-1.5">{lbl('Ad', true)}<Input {...fi('first_name')} /></div>
                <div className="space-y-1.5">{lbl('Soyad', true)}<Input {...fi('last_name')} /></div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <CreatableCombobox label="Departman" value={form.department_id || null}
                  onChange={v => setForm(p => ({ ...p, department_id: v, position_id: null }))}
                  options={departments.map(d => ({ value: d.id, label: d.name }))}
                  onCreateOption={createDepartment} placeholder="Seç veya ekle..." />
                <CreatableCombobox label="Pozisyon" value={form.position_id || null}
                  onChange={v => setForm(p => ({ ...p, position_id: v }))}
                  options={positions.filter(p => !form.department_id || p.department_id === form.department_id || !p.department_id).map(p => ({ value: p.id, label: p.name }))}
                  onCreateOption={createPosition} placeholder="Seç veya ekle..." />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-1.5">
                  {lbl('Çalışma Türü')}
                  <select value={form.employment_type} onChange={e => setForm(p => ({ ...p, employment_type: e.target.value }))}
                    className="w-full h-9 px-3 rounded-md border border-[#d0c4b0] bg-white text-sm text-[#1a1916] focus:outline-none focus:ring-2 focus:ring-[#2e2d29]/15">
                    <option value="full_time">Tam Zamanlı</option>
                    <option value="part_time">Yarı Zamanlı</option>
                    <option value="contractor">Sözleşmeli</option>
                  </select>
                </div>
                <div className="space-y-1.5">{lbl('İşe Giriş Tarihi')}<Input {...fi('hire_date', 'date')} /></div>
              </div>
            </div>
          ) : (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-1.5">{lbl('Kimlik No')}<Input {...fi('national_id')} /></div>
                <div className="space-y-1.5">{lbl('Doğum Tarihi')}<Input {...fi('birth_date', 'date')} /></div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-1.5">{lbl('Telefon')}<Input {...fi('phone', 'tel')} /></div>
                <div className="space-y-1.5">{lbl('E-posta')}<Input {...fi('email', 'email')} /></div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-1.5">{lbl('Sigorta Sicil No')}<Input {...fi('insurance_number')} /></div>
                <div className="space-y-1.5">{lbl('Banka')}<Input {...fi('bank_name')} /></div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-1.5">{lbl('Hesap No / IBAN')}<Input {...fi('bank_account_number')} /></div>
                <div className="space-y-1.5">{lbl('Acil Kişi')}<Input {...fi('emergency_contact_name')} /></div>
              </div>
              <div className="space-y-1.5">{lbl('Acil Tel')}<Input {...fi('emergency_contact_phone', 'tel')} /></div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 pb-5 flex items-center justify-between border-t border-[#e8dfd0] pt-4">
          <Button variant="outline" onClick={step === 1 ? onClose : () => setStep(1)} className="border-[#d0c4b0]">
            {step === 1 ? 'İptal' : '← Geri'}
          </Button>
          {step === 1 ? (
            <Button onClick={() => setStep(2)} disabled={!canNext}
              className="bg-[#2e2d29] text-[#f5f0e8] hover:bg-[#1a1916]">
              Devam <ChevronRight size={14} className="ml-1" />
            </Button>
          ) : (
            <Button onClick={onSave} disabled={loading} data-testid="save-employee-btn"
              className="bg-[#3d6e4e] text-white hover:bg-[#2d5e3e]">
              {loading ? <><Loader2 size={14} className="animate-spin mr-2" />Kaydediliyor...</> : <><Check size={14} className="mr-1.5" /> Kaydet</>}
            </Button>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
